import { useDatoClient } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { fetchDatoSpeakers } from '@src/utility/speakerUtils';
import { useCallback, useEffect, useState } from 'react';

import type { Img } from '@src/components/common/PhotosModal';

const logger = LogManager.getLogger('hook:useHeadshotManager');

export interface HeadshotSpeaker {
  id: string;
  name: string;
  headshots: Array<{ id: string; url: string }>;
}

export interface HeadshotManager {
  /** Flattened headshot images for the photo grid */
  headshotImages: Img[];
  /** Speakers that have headshots or can receive uploads */
  headshotSpeakers: HeadshotSpeaker[];
  /** Whether a headshot upload is in progress */
  isUploadingHeadshot: boolean;
  /** Currently selected speaker ID for uploads */
  uploadSpeakerId: string;
  /** Set the target speaker ID for the next upload */
  setUploadSpeakerId: (id: string) => void;
  /** Upload a headshot file to a speaker (uses override or current uploadSpeakerId) */
  handleHeadshotUpload: (
    file: File,
    speakerIdOverride?: string,
  ) => Promise<void>;
}

/**
 * Hook that encapsulates headshot fetching, state management, and upload logic
 * previously inlined in PhotosModal.
 *
 * Uploads are routed through SpeakerRepository (the caller's context provides it).
 * When Step 1 integrates SpeakerService more broadly, the upload path can be
 * swapped to use the service without touching the consuming component.
 */
export function useHeadshotManager(enabled: boolean): HeadshotManager {
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();

  const [headshotImages, setHeadshotImages] = useState<Img[]>([]);
  const [headshotSpeakers, setHeadshotSpeakers] = useState<HeadshotSpeaker[]>(
    [],
  );
  const [isUploadingHeadshot, setIsUploadingHeadshot] = useState(false);
  const [uploadSpeakerId, setUploadSpeakerId] = useState<string>('');

  const refreshHeadshotImages = useCallback(
    (
      speakers: Array<{
        id: string;
        name?: string;
        headshots?: Array<{ id: string; url: string }>;
      }>,
    ) => {
      const images: Img[] = speakers.flatMap(s =>
        (s.headshots || []).map(h => ({
          url: h.url,
          alt: s.name || 'Speaker headshot',
          width: '',
          height: '',
          description: s.name || 'Speaker headshot',
        })),
      );
      setHeadshotImages(images);
      setHeadshotSpeakers(
        speakers
          .filter(s => s.id && s.name)
          .map(s => ({
            id: s.id,
            name: s.name || '',
            headshots: s.headshots || [],
          })),
      );
    },
    [],
  );

  // Fetch speakers on mount when enabled
  useEffect(() => {
    if (!enabled) return;
    const storyId = videoCreator.story?.id;
    if (!storyId) return;

    let cancelled = false;
    fetchDatoSpeakers(storyId, datoClientStore)
      .then(speakers => {
        if (cancelled) return;
        refreshHeadshotImages(speakers);
      })
      .catch(err => {
        logger.warn('Failed to fetch speaker headshots', { err });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, videoCreator.story?.id, datoClientStore, refreshHeadshotImages]);

  const handleHeadshotUpload = useCallback(
    async (file: File, speakerIdOverride?: string) => {
      const targetSpeakerId = speakerIdOverride || uploadSpeakerId;
      if (!targetSpeakerId) return;
      const speakerRepo = datoClientStore?.speakerRepository;
      if (!speakerRepo) return;

      setIsUploadingHeadshot(true);
      try {
        const upload = await speakerRepo.uploadHeadshot(file);
        if (!upload) return;

        // Get existing headshot IDs for this speaker
        const speaker = headshotSpeakers.find(s => s.id === targetSpeakerId);
        const existingUploadIds = (speaker?.headshots || []).map(h => ({
          upload_id: h.id,
        }));

        // Update speaker with existing + new headshot
        await speakerRepo.updateSpeaker(targetSpeakerId, {
          headshots: [...existingUploadIds, { upload_id: upload.id }],
        });

        // Update local state directly (CDN may return stale cached data)
        const speakerName = speaker?.name || 'Speaker headshot';
        setHeadshotSpeakers(prev =>
          prev.map(s =>
            s.id === targetSpeakerId
              ? {
                  ...s,
                  headshots: [
                    ...s.headshots,
                    { id: upload.id, url: upload.url },
                  ],
                }
              : s,
          ),
        );
        setHeadshotImages(prev => [
          ...prev,
          {
            url: upload.url,
            alt: speakerName,
            width: '',
            height: '',
            description: speakerName,
          },
        ]);
      } catch (err) {
        logger.error('Failed to upload headshot', extractErrorInfo(err));
      } finally {
        setIsUploadingHeadshot(false);
      }
    },
    [uploadSpeakerId, headshotSpeakers, datoClientStore],
  );

  return {
    headshotImages,
    headshotSpeakers,
    isUploadingHeadshot,
    uploadSpeakerId,
    setUploadSpeakerId,
    handleHeadshotUpload,
  };
}
